import type { AttrPaymentMethod } from '../../types/attributes';

/**
 * The filter half of the panel, typed structurally so the comparison table and
 * the card list can each pass their own product shape — the same reason
 * `RankableAttribute` is structural on the sort side.
 */

/** Which source a group's values are picked from. */
export type FilterType =
  | 'PRODUCT_ATTRIBUTES'
  | 'OFFERS'
  | 'PROVIDERS'
  | 'PRODUCT_TAGS';

/** How a product attribute's value is compared. */
export type FilterValueOperator =
  | 'DEFINED'
  | 'NOT_DEFINED'
  | 'EQUALS'
  | 'LESS_THAN'
  | 'GREATER_THAN'
  | 'BETWEEN';

/** How several selections inside one group combine. */
export type FilterCombinator = 'OR' | 'AND';

export type FilterOption = {
  /**
   * The value matched against the product: an attribute's uid on an authored
   * group, or the attribute's own value on a derived one.
   *
   * **Not the option's identity.** Several options in one group legitimately
   * share it — every band over the annual fee names the same attribute — so
   * selection, availability and counts key off `optionKeyOf` instead.
   */
  value: string;
  /**
   * Identity of this option within its group, and what the URL carries.
   * Editor-authored, because an ad URL pins it once this ships.
   *
   * Optional only because a derived group has none to author: there the value
   * is the product's own and is already unique, so it stands in.
   *
   * ⚠️ The fallback means a band set authored with **no** keys collapses again,
   * since every band names one attribute and uniqueness cannot catch two absent
   * keys. Nothing upstream backfills one, so `optionKeyOf` below is the only
   * fallback and an editor has to author a key on every option that shares an
   * attribute. Options sharing one key also share one count, so `COUNT` value
   * sorting sees them as a single row.
   */
  key?: string;
  /**
   * Which kind this option is, and so which product field it matches against.
   * Carried per option so one group can mix kinds — an attribute value beside a
   * tag value. Absent on an option authored before the kind moved here, which
   * matches nothing rather than being guessed at.
   */
  filterType?: FilterType;
  label?: string;
  operator?: FilterValueOperator;
  operand?: string;
  /**
   * Inclusive upper bound of a `BETWEEN` band, with `operand` as its lower one.
   * A band is one row the reader ticks ("HK$1,001-3,000"), so both bounds sit
   * on the one option rather than on two the reader would have to combine.
   */
  operandEnd?: string;
  /**
   * Options in one manually listed category sharing a `groupKey` render as one
   * row (CMS-2087). The key is used as it arrives: the producer sends a
   * lowercase slug, or nothing for an ungrouped option.
   */
  groupKey?: string;
  /**
   * The label of the row a `groupKey` forms, set on every member by the
   * producer. It is the only source of that row's label: with none, the row
   * renders its key like any option without a `label`.
   */
  groupLabel?: string;
  /**
   * Present only on a row built by `groupFilterOptions`: the authored options it
   * stands for. The row matches a product carrying any of them; its `value` is
   * just the group key.
   */
  members?: FilterOption[];
  /**
   * On a grouped row: each member's own key, still valid in a URL so a link
   * written before the group existed selects it.
   */
  aliases?: string[];
};

export type FilterCategory = {
  /** Identifier used in the URL. A group without one cannot be selected. */
  categoryId?: string;
  name?: string;
  combinator: FilterCombinator;
  /** Options come from the products rather than from `options`. */
  autoGenerateValues: boolean;
  /**
   * Which source a derived group enumerates. Only meaningful with
   * `autoGenerateValues`: a derived group has no options to carry a kind, so
   * this is the one place a group still names one.
   */
  autoGenerateFrom?: FilterType;
  /** The attribute whose distinct values become the options when derived. */
  valuesSourceAttribute?: string;
  options: FilterOption[];
  defaultExpanded: boolean;
  collapseAt?: number;
  /** The group's own show-more / show-less copy; the panel supplies a fallback. */
  collapsedLabel?: string;
  expandedLabel?: string;
  /**
   * Chips mode is navigation to category pages rather than filtering, so a
   * chips group carries no options and the panel renders none.
   */
  displayMode?: 'CHECKBOX' | 'CHIPS';
  /** Display order of the options at render. */
  valueSorting?: 'MANUAL' | 'ALPHA' | 'COUNT';
  /**
   * A chips group's destinations. Navigation to category pages rather than
   * filters (CMS-1893 R3.2), so these never enter the predicate.
   */
  chips: { label: string; url: string }[];
};

/**
 * How an option is identified in a selection, a count and an availability set.
 *
 * Keyed on the editor's `key` where there is one, because `value` is the source
 * uid and several options can share it — with `value` as the key, four annual
 * fee bands collapsed into whichever the engine saw last, and the URL could not
 * name which of them the reader had ticked.
 */
export const optionKeyOf = (option: FilterOption): string =>
  option.key ?? option.value;

/** What the reader has ticked, keyed by `categoryId`. Values, never labels. */
export type FilterSelection = Readonly<Record<string, readonly string[]>>;

/**
 * The product surface filtering reads.
 *
 * Both naming conventions are accepted because the two callers genuinely
 * differ: this package's own `CmsProduct` is snake_case (`provider_uid`,
 * `exclusive_offers`) while the shape fe-services maps from GraphQL is
 * camelCase (`providerUid`, `exclusiveOffers`). Normalising in one place here
 * is cheaper than a translation layer at each call site, and a caller passing
 * neither simply matches nothing rather than failing to compile.
 */
type SnakeOffer = { uid?: string | null; offer_title?: string | null };
type CamelOffer = { offerUid?: string | null; offerTitle?: string | null };

export type FilterableProduct = {
  uid?: string | null;
  productUid?: string | null;

  category_identifiers?: readonly (string | null)[] | null;
  categoryIdentifiers?: readonly (string | null)[] | null;

  provider?:
    | ({ provider_uid?: string | null; provider_name?: string | null } & {
        providerUid?: string | null;
        providerName?: string | null;
      })
    | null;

  exclusive_offers?: readonly (SnakeOffer | null)[] | null;
  bundle_exclusive_offers?: readonly (SnakeOffer | null)[] | null;
  exclusiveOffers?: readonly (CamelOffer | null)[] | null;
  bundleExclusiveOffers?: readonly (CamelOffer | null)[] | null;

  attributes?:
    | readonly ({
        name?: { name?: string | null } | null;
        value?: unknown;
        value_start?: unknown;
        /** Linked PAYMENT METHOD entries of a selection attribute (CMS-1455). */
        paymentMethods?: AttrPaymentMethod[];
      } | null)[]
    | null;
};

/** The product's identity for deduplication, under either convention. */
export const productIdOf = (product: FilterableProduct): string | undefined =>
  product.productUid ?? product.uid ?? undefined;

/** Provider uid under either convention. */
export const providerUidOf = (product: FilterableProduct): string | undefined =>
  product.provider?.providerUid ?? product.provider?.provider_uid ?? undefined;

/** Provider display name under either convention. */
export const providerNameOf = (
  product: FilterableProduct,
): string | undefined =>
  product.provider?.providerName ??
  product.provider?.provider_name ??
  undefined;

const offersOf = (product: FilterableProduct) =>
  [
    ...(product.exclusiveOffers ?? []),
    ...(product.bundleExclusiveOffers ?? []),
    ...(product.exclusive_offers ?? []),
    ...(product.bundle_exclusive_offers ?? []),
  ].flatMap((offer) => {
    const uid =
      (offer as CamelOffer | null)?.offerUid ??
      (offer as SnakeOffer | null)?.uid;
    const title =
      (offer as CamelOffer | null)?.offerTitle ??
      (offer as SnakeOffer | null)?.offer_title;
    return uid ? [{ uid, title: title?.trim() || undefined }] : [];
  });

/** Every offer uid the product carries, bundle offers included. */
export const offerUidsOf = (product: FilterableProduct): string[] =>
  offersOf(product).map((offer) => offer.uid);

/** The reader-facing title of one of the product's offers, if it has one. */
export const offerTitleOf = (
  product: FilterableProduct,
  uid: string,
): string | undefined =>
  offersOf(product).find((offer) => offer.uid === uid)?.title;

/** Product tags under either convention. */
export const tagsOf = (product: FilterableProduct): string[] =>
  [
    ...(product.categoryIdentifiers ?? []),
    ...(product.category_identifiers ?? []),
  ].flatMap((tag) => (tag ? [tag] : []));
